const LCW_I18N = window.lcwI18n || {};
/*
 * LCW product page behaviour (loaded on product pages by the LCW size guide block; the guide itself
 * lives in lcw-size-guide.js).
 *  1. Colour label: "Color: Black" follows a swatch the moment it's picked, before Horizon's re-render.
 *  2. Size gate: no size is pre-selected on ordinary product entry. Until the shopper picks a size,
 *     Add to cart (main + sticky) moves focus to the size options instead of silently adding the first
 *     available size, and Buy it now / wallet buttons are locked. A valid ?variant= link keeps its size.
 *     A colour change keeps the chosen size where that colour comes in it; otherwise the size is
 *     cleared and the shopper is told why (never swapped for another size).
 *  3. Sticky purchase panel: its height is measured so lcw.css can stick a panel that is taller than
 *     the screen by its bottom edge, keeping every part of it reachable.
 *  4. Hero gallery (Horizon carousel with a thumbnail rail): the zoom button opens the viewer on the
 *     photo shown; arrow keys step through photos (Up / Down / Home / End too on the rail); the
 *     current thumbnail carries aria-current and the zoom button's label names the current photo.
 *  5. Made to order: "How it works" opens "Our made-to-order process" and moves focus to it; a variant
 *     with its own Fulfillment profile swaps the note before Add to cart.
 *  6. Phone purchase bar: hidden while a text field has focus (on-screen keyboard) and while Shopify's
 *     cookie banner shows; lcw.css also hides it under modal dialogs and the menu drawer.
 * Every listener is delegated from document, so re-rendered sections never get duplicate handlers.
 */

// Option names that are the size choice (the Size Guide reads the same list)
const SIZE_NAMES = ['size', 'taille', 'größe', 'waist', 'waist size'];
const COLOUR_NAMES = ['color', 'colour'];

/** @param {HTMLInputElement} input */
const optionName = (input) => (input.dataset.optionName || '').trim().toLowerCase();

/* ---------- 1. Colour label ---------- */

document.addEventListener('change', (event) => {
  const input = /** @type {HTMLInputElement} */ (event.target);
  if (!(input instanceof HTMLInputElement) || !input.matches('input[type="radio"][data-option-name]')) return;
  if (!COLOUR_NAMES.includes(input.dataset.optionName.trim().toLowerCase())) return;
  const name = input.closest('fieldset')?.querySelector('[data-lcw-colour-name]');
  if (name) name.textContent = input.dataset.lcwColourLabel || input.value;
});

/* ---------- 2. Size gate ---------- */

const gate = {
  // A size counts as chosen only when the page came from a valid ?variant= link (the picker then
  // carries data-lcw-url-variant) or once the shopper picks one. Set in initGate().
  open: false,
  root: /** @type {HTMLElement|null} */ (null),
};

function mainPicker() {
  return /** @type {HTMLElement|null} */ (
    gate.root?.querySelector('variant-picker[data-template-product-match], variant-picker') ?? null
  );
}

function sizeFieldset() {
  const picker = mainPicker();
  if (!picker) return null;
  return [...picker.querySelectorAll('fieldset')].find((fs) => {
    const input = fs.querySelector('input[type="radio"][data-option-name]');
    return input && SIZE_NAMES.includes(optionName(input));
  }) || null;
}

/**
 * Buy it now and the wallet buttons follow the gate: they can't buy a size nobody picked. The buttons
 * go inert (no clicks, no focus) and dim; a tap on them lands on their wrapper, which asks for a size
 * the way Add to cart does. Shopify's own markup and branding stay as they are.
 * @param {boolean} closed
 */
function lockExpress(closed) {
  const block = gate.root?.querySelector('.accelerated-checkout-block');
  if (!block) return;
  block.classList.toggle('lcw-express--locked', closed);
  for (const child of block.children) child.toggleAttribute('inert', closed);
}

/**
 * A polite note under the options, e.g. why a chosen size was cleared. Placed after the picker, which
 * Horizon re-renders on every option change, so the note isn't wiped by that re-render.
 * @param {string} text
 */
function setSizeNote(text) {
  const picker = mainPicker();
  if (!picker) return;
  let note = picker.nextElementSibling?.classList.contains('lcw-size-note') ? picker.nextElementSibling : null;
  if (!text) {
    note?.remove();
    return;
  }
  if (!note) {
    note = document.createElement('p');
    note.className = 'lcw-size-note';
    note.setAttribute('role', 'status');
    picker.after(note);
  }
  // Set the text after the live region exists, so screen readers announce it
  requestAnimationFrame(() => {
    if (note) note.textContent = text;
  });
}

function hasAvailableSize(fs) {
  return [...fs.querySelectorAll('input[type="radio"]')].some((i) => i.dataset.optionAvailable === 'true');
}

function setButtonLabels(closed) {
  const main = gate.root?.querySelector('.add-to-cart__text');
  if (main) {
    if (!main.dataset.lcwOriginal) main.dataset.lcwOriginal = main.textContent.trim();
    const want = closed ? (LCW_I18N.selectSize || 'Select a size') : main.dataset.lcwOriginal;
    if (main.textContent.trim() !== want) main.textContent = want;
  }
  const sticky = document.querySelector('sticky-add-to-cart .add-to-cart-text__content > span:first-child');
  if (sticky && closed && sticky.textContent.trim() !== (LCW_I18N.selectSize || 'Select a size')) {
    sticky.dataset.lcwOriginal = sticky.textContent.trim();
    sticky.textContent = (LCW_I18N.selectSize || 'Select a size');
  } else if (sticky && !closed && sticky.dataset.lcwOriginal) {
    sticky.textContent = sticky.dataset.lcwOriginal;
    delete sticky.dataset.lcwOriginal;
  }
  setStickyVariantLabel(closed);
  lockExpress(closed);
}

const SIZE_PLACEHOLDER = (LCW_I18N.sizeNotSelected || 'Size not selected');

/** Currently selected colour, read from selection state (never from the label we render). */
function selectedColour(fallbackVariantTitle) {
  const picker = mainPicker();
  const checked = picker && [...picker.querySelectorAll('input[type="radio"]:checked')].find((input) =>
    COLOUR_NAMES.includes(optionName(input))
  );
  if (checked) return checked.value;
  // No colour control (e.g. a colour picker set to dropdowns): use the colour part of Horizon's variant title
  return (fallbackVariantTitle || '').split('/')[0].trim();
}

/**
 * Sticky bar variant line. Idempotent: the same selection state always produces the same text,
 * so the MutationObserver that re-runs this cannot feed on its own output.
 */
function setStickyVariantLabel(closed) {
  const el = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__variant');
  if (!el) return;
  const current = el.textContent.trim();
  // Remember Horizon's own title ("Black / S"), never the placeholder we write
  if (!current.includes(SIZE_PLACEHOLDER)) el.dataset.lcwVariantTitle = current;
  const horizonTitle = el.dataset.lcwVariantTitle || '';

  let want;
  if (closed) {
    const colour = selectedColour(horizonTitle);
    want = colour ? `${colour} · ${SIZE_PLACEHOLDER}` : SIZE_PLACEHOLDER;
  } else {
    if (!current.includes(SIZE_PLACEHOLDER)) return; // Horizon already shows the chosen variant
    // Build "Colour / Size" from the checked options so a stale title is never shown
    const picker = gate.root?.querySelector('variant-picker');
    const values = picker ? [...picker.querySelectorAll('fieldset input[type="radio"]:checked')].map((i) => i.value) : [];
    const colour = selectedColour(horizonTitle);
    if (values.length && colour && !values.includes(colour)) values.unshift(colour);
    want = values.length ? values.join(' / ') : horizonTitle;
  }
  if (want && current !== want) el.textContent = want;
}

/**
 * While no size is chosen, options listed after Size (e.g. a Color option in second place) can't be
 * "sold out in this size" or "not made in this size" yet: those states were rendered for the size
 * Shopify picked as a default, which the gate has just unselected. Show them neutral until a size is
 * picked; Horizon's re-render then brings back the real states for that size.
 */
function clearSizeDependentStates(fs) {
  const picker = fs.closest('variant-picker');
  if (!picker) return;
  picker.querySelectorAll('fieldset.lcw-colour').forEach((colourFieldset) => {
    if (!(fs.compareDocumentPosition(colourFieldset) & Node.DOCUMENT_POSITION_FOLLOWING)) return;
    colourFieldset.querySelectorAll('.lcw-colour__option--missing, .lcw-colour__option--soldout').forEach((option) => {
      option.classList.remove('lcw-colour__option--missing', 'lcw-colour__option--soldout');
      const input = option.querySelector('input');
      input?.removeAttribute('aria-disabled');
      input?.setAttribute('aria-label', input.value);
    });
  });
  const status = picker.querySelector('.lcw-variant-status');
  if (status && status.textContent.trim()) status.textContent = '';
}

function applyGate() {
  const fs = sizeFieldset();
  if (!fs) return;
  const closed = !gate.open && hasAvailableSize(fs);
  fs.toggleAttribute('data-lcw-size-required', closed);
  if (closed) {
    fs.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      input.checked = false;
      input.dataset.currentChecked = 'false';
    });
    clearSizeDependentStates(fs);
  }
  setButtonLabels(closed);
}

function showSizeError() {
  const fs = sizeFieldset();
  if (!fs) return;
  setSizeNote('');
  let msg = fs.querySelector('.lcw-size-error');
  if (!msg) {
    msg = document.createElement('p');
    msg.className = 'lcw-size-error';
    msg.id = 'lcw-size-error';
    msg.setAttribute('role', 'alert');
    fs.appendChild(msg);
  }
  msg.textContent = (LCW_I18N.pleaseSelectSize || 'Please select a size.');
  fs.querySelectorAll('input[type="radio"]').forEach((i) => i.setAttribute('aria-describedby', 'lcw-size-error'));
  fs.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  const first = fs.querySelector('input[data-option-available="true"]') || fs.querySelector('input[type="radio"]');
  first?.focus({ preventScroll: true });
}

function isAddToCart(el) {
  if (!gate.root) return false;
  const btn = el.closest('button');
  if (!btn) return false;
  if (btn.closest('sticky-add-to-cart')) return true;
  return gate.root.contains(btn) && (btn.name === 'add' || btn.matches('[ref="addToCartButton"]'));
}

// Capture phase: runs before Horizon's own add-to-cart handlers
document.addEventListener(
  'click',
  (event) => {
    if (gate.open || !isAddToCart(/** @type {HTMLElement} */ (event.target))) return;
    const fs = sizeFieldset();
    if (!fs || !fs.hasAttribute('data-lcw-size-required')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showSizeError();
  },
  true
);

// A tap on locked express buttons lands on their wrapper (the buttons are inert): ask for a size
document.addEventListener(
  'click',
  (event) => {
    const block = event.target instanceof Element ? event.target.closest('.accelerated-checkout-block.lcw-express--locked') : null;
    if (!block || gate.open || !gate.root?.contains(block)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showSizeError();
  },
  true
);

document.addEventListener('change', (event) => {
  const input = /** @type {HTMLInputElement} */ (event.target);
  if (!input.matches?.('input[type="radio"][data-option-name]')) return;
  if (!SIZE_NAMES.includes(optionName(input))) return;
  if (!gate.root?.contains(input)) return;
  gate.open = true;
  const fs = sizeFieldset();
  fs?.removeAttribute('data-lcw-size-required');
  fs?.querySelector('.lcw-size-error')?.remove();
  fs?.querySelectorAll('input[aria-describedby="lcw-size-error"]').forEach((i) => i.removeAttribute('aria-describedby'));
  setSizeNote('');
  setButtonLabels(false);
});

/*
 * A colour change keeps the chosen size where that colour comes in it (in stock or sold out, which the
 * buttons then show). Where it doesn't, Shopify answers with no variant, or with another size: the size
 * is cleared instead of silently swapped, the shopper is told why, and a colour without a variant is
 * loaded on its own, so the form holds a real variant of it again.
 */
let colourChange = /** @type {{ size: string } | null} */ (null);

// Capture phase: read the chosen size before Horizon's handler starts the change
document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('input[type="radio"][data-option-name]')) return;
    if (!gate.open || !gate.root?.contains(input) || !COLOUR_NAMES.includes(optionName(input))) return;
    const size = /** @type {HTMLInputElement|null} */ (sizeFieldset()?.querySelector('input[type="radio"]:checked') ?? null);
    colourChange = size ? { size: size.value } : null;
  },
  true
);

document.addEventListener('shopify:product:select', (event) => {
  const change = colourChange;
  colourChange = null;
  const origin = event.target instanceof Element ? event.target : null;
  if (!change || !origin || !gate.root?.contains(origin) || !(/** @type {any} */ (event).promise)) return;
  /** @type {any} */ (event).promise
    .then((/** @type {any} */ result) => requestAnimationFrame(() => settleSize(change.size, result?.detail?.resource ?? null)))
    .catch(() => {});
});

/**
 * @param {string} size - The size chosen before the colour change
 * @param {any} resource - The variant Shopify answered with (null: no variant for this combination)
 */
function settleSize(size, resource) {
  const fs = sizeFieldset();
  if (!fs || !gate.open) return;
  const checked = /** @type {HTMLInputElement|null} */ (fs.querySelector('input[type="radio"]:checked'));
  // The checked size carries the id of the variant for this exact selection (none when it isn't made)
  const exact = checked?.dataset.variantId || '';
  if (resource && checked?.value === size && exact && String(resource.id) === exact) return;
  const colour = selectedColour('');
  gate.open = false;
  applyGate();
  // The URL no longer names a variant: a refresh must not bring the cleared size back
  const url = new URL(window.location.href);
  if (url.searchParams.has('variant')) {
    url.searchParams.delete('variant');
    history.replaceState(history.state, '', url.toString());
  }
  setSizeNote(colour ? `${size} isn't available in ${colour}. Please select a size.` : `${size} isn't available. Please select a size.`);
  if (resource) return;
  const picker = /** @type {any} */ (mainPicker());
  const colourInput = picker?.querySelector('fieldset input[type="radio"]:checked');
  if (picker?.fetchUpdatedSection && picker.buildRequestUrl && colourInput) {
    picker.fetchUpdatedSection(picker.buildRequestUrl(colourInput));
  }
}

function initGate() {
  gate.root = document.querySelector('.product-information product-component, product-component');
  if (!gate.root) return;
  gate.open = Boolean(mainPicker()?.dataset.lcwUrlVariant);
  applyGate();
  // Horizon morphs the picker and buttons after every option change: re-apply while closed.
  let queued = false;
  const observer = new MutationObserver(() => {
    if (gate.open || queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      applyGate();
    });
  });
  observer.observe(gate.root, { subtree: true, childList: true, attributes: true, attributeFilter: ['checked', 'disabled'] });
  const sticky = document.querySelector('sticky-add-to-cart');
  if (sticky) observer.observe(sticky, { subtree: true, childList: true, characterData: true });
}

/* ---------- 3. Sticky purchase panel height ---------- */

/* The panel is sticky only while it fits on screen below the header (data-lcw-fits); a taller panel
   (e.g. with an accordion open) scrolls with the page, so no purchase control is ever out of reach. */
function watchPanelHeight() {
  const panel = document.querySelector('.product-information .product-details.sticky-content--desktop');
  if (!(panel instanceof HTMLElement) || !('ResizeObserver' in window)) return;
  let height = 0;
  const update = () => {
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 84;
    panel.toggleAttribute('data-lcw-fits', height > 0 && height <= window.innerHeight - header - 32);
  };
  new ResizeObserver(([entry]) => {
    const next = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height);
    if (next === height) return;
    height = next;
    panel.style.setProperty('--lcw-details-height', `${height}px`);
    update();
  }).observe(panel);
  window.addEventListener('resize', update, { passive: true });
}

/* ---------- 4. Hero gallery (lcw-pdp carousel) ---------- */

const GALLERY = '.lcw-pdp media-gallery.media-gallery--carousel';

/** The gallery around `el`, with its slideshow and zoom viewer (Horizon components). */
function galleryParts(el) {
  const gallery = el instanceof Element ? el.closest(GALLERY) : null;
  if (!gallery) return null;
  return {
    gallery,
    slideshow: /** @type {any} */ (gallery.querySelector('slideshow-component')),
    zoom: /** @type {any} */ (gallery.querySelector('zoom-dialog')),
  };
}

// The native dialog hands focus back to this button when the viewer closes
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-lcw-zoom]') : null;
  const parts = button && galleryParts(button);
  if (!parts || typeof parts.zoom?.open !== 'function') return;
  parts.zoom.open(parts.slideshow?.current ?? 0, event);
});

document.addEventListener('keydown', (event) => {
  if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
  const target = /** @type {Element} */ (event.target);
  const parts = galleryParts(target);
  // The zoom viewer handles its own keys
  if (!parts?.slideshow || target.closest('dialog')) return;
  const onRail = target.closest('.slideshow-controls__thumbnail');
  const rtl = getComputedStyle(parts.gallery).direction === 'rtl';
  const count = parts.gallery.querySelectorAll('slideshow-slide').length;
  const current = parts.slideshow.current ?? 0;
  let index = null;
  if (event.key === 'ArrowRight' || (onRail && event.key === 'ArrowDown')) index = current + (rtl && event.key === 'ArrowRight' ? -1 : 1);
  else if (event.key === 'ArrowLeft' || (onRail && event.key === 'ArrowUp')) index = current + (rtl && event.key === 'ArrowLeft' ? 1 : -1);
  else if (onRail && event.key === 'Home') index = 0;
  else if (onRail && event.key === 'End') index = count - 1;
  if (index === null || count < 2) return;
  event.preventDefault();
  index = (index + count) % count;
  parts.slideshow.select(index, event);
  if (onRail) parts.gallery.querySelectorAll('.slideshow-controls__thumbnail')[index]?.focus();
});

/** aria-current on the thumbnail shown, and the photo number in the zoom button's label. */
function syncGallery(gallery) {
  const index = /** @type {any} */ (gallery.querySelector('slideshow-component'))?.current ?? 0;
  gallery.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumb, i) => {
    if (i === index) thumb.setAttribute('aria-current', 'true');
    else thumb.removeAttribute('aria-current');
  });
  const zoom = gallery.querySelector('[data-lcw-zoom]');
  const label = zoom?.getAttribute('aria-label');
  if (zoom && label) {
    const next = label.replace(/\d+/, String(index + 1));
    if (next !== label) zoom.setAttribute('aria-label', next);
  }
}

function watchGallery() {
  document.querySelectorAll(GALLERY).forEach(syncGallery);
  // Horizon marks the shown thumbnail with aria-selected, also after the gallery is re-rendered
  new MutationObserver((records) => {
    const galleries = new Set();
    for (const record of records) {
      const gallery = record.target instanceof Element ? record.target.closest(GALLERY) : null;
      if (gallery) galleries.add(gallery);
    }
    galleries.forEach(syncGallery);
  }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
}

/* ---------- 5. Made to order ---------- */

/**
 * Opens "Our made-to-order process" (Product details), optionally brings it into view below the
 * sticky header, and moves focus to its summary so keyboard users land on it.
 * @param {boolean} scroll
 * @returns {boolean} whether the section exists
 */
function openProcess(scroll) {
  const process = document.getElementById('MadeToOrder');
  if (!(process instanceof HTMLDetailsElement)) return false;
  process.open = true;
  // It may sit inside the Shipping & Returns accordion: open that too
  for (let parent = process.parentElement?.closest('details'); parent; parent = parent.parentElement?.closest('details')) {
    parent.open = true;
  }
  if (scroll) {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    process.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }
  process.querySelector('summary')?.focus({ preventScroll: true });
  return true;
}

// "Read more" beside the introduction: opens Details & Materials and brings it into view
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-lcw-acc-open]') : null;
  const item = link && document.getElementById(link.getAttribute('data-lcw-acc-open') || '');
  if (!(item instanceof HTMLDetailsElement)) return;
  event.preventDefault();
  item.open = true;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  item.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  item.querySelector('summary')?.focus({ preventScroll: true });
});

// "How it works" in the note before Add to cart
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-lcw-mto-link]') : null;
  if (link && openProcess(true)) event.preventDefault();
});

// Horizon re-renders only parts of the product form on a variant change. A variant with its own
// Fulfillment profile swaps the note; the same profile leaves it untouched.
document.addEventListener('shopify:product:select', (event) => {
  const origin = event.target instanceof Element ? event.target : null;
  if (!origin || origin.closest('product-card') || !event.promise) return;
  const scope = origin.closest('.shopify-section, dialog') || document;
  if (!scope.querySelector('[data-lcw-mto-note]')) return;
  event.promise
    .then(({ detail }) => {
      const fresh = detail?.html?.querySelector('[data-lcw-mto-note]');
      const current = scope.querySelector('[data-lcw-mto-note]');
      if (fresh && current && current.dataset.lcwMtoProfile !== fresh.dataset.lcwMtoProfile) {
        current.replaceWith(document.importNode(fresh, true));
      }
    })
    .catch(() => {});
});

/* ---------- 6. Phone purchase bar: out of the way ---------- */

// Text fields bring up the on-screen keyboard: the fixed bar would sit on top of it
const TEXT_ENTRY =
  'input:not([type="radio"], [type="checkbox"], [type="button"], [type="submit"], [type="hidden"], [type="range"], [type="color"], [type="file"], [type="image"], [type="reset"]), textarea, [contenteditable="true"]';

document.addEventListener('focusin', (event) => {
  const target = event.target;
  if (target instanceof Element && target.matches(TEXT_ENTRY) && !target.closest('sticky-add-to-cart')) {
    document.documentElement.classList.add('lcw-typing');
  }
});

document.addEventListener('focusout', (event) => {
  if (event.target instanceof Element && event.target.matches(TEXT_ENTRY)) {
    document.documentElement.classList.remove('lcw-typing');
  }
});

/** Shopify's cookie banner sits at the bottom of the screen too: the bar waits until it's answered. */
function watchConsentBanner() {
  if (!document.querySelector('sticky-add-to-cart')) return;
  /** @type {MutationObserver | null} */
  let bannerObserver = null;
  const update = () => {
    const banner = document.getElementById('shopify-pc__banner');
    document.documentElement.classList.toggle('lcw-consent-open', Boolean(banner?.checkVisibility?.()));
    if (banner && !bannerObserver) {
      bannerObserver = new MutationObserver(update);
      bannerObserver.observe(banner, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
    }
  };
  update();
  new MutationObserver(update).observe(document.body, { childList: true });
  // The banner script may add it inside another element, after load
  setTimeout(update, 2500);
}

function init() {
  initGate();
  watchPanelHeight();
  watchGallery();
  watchConsentBanner();
  if (location.hash === '#MadeToOrder') openProcess(false);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
